import React, { useEffect, useMemo, useState } from 'react';

import { Button, Card, Center, Group, LoadingOverlay, Stack, Text } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { IconDownload } from '@tabler/icons-react';
import PageHeader from 'components/PageHeader';
import PackageStatusBadge from 'components/status-badges/PackageStatusBadge';
import Table from 'components/Table';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import useCommittedClubProgram from 'hooks/committedClub/useCommittedClubProgram';
import useCommittedClubTracking from 'hooks/committedClub/useCommittedClubTracking';
import useCommittedClubTrackingCsv from 'hooks/committedClub/useCommittedClubTrackingCsv';
import useCommittedClubTrackingSummary from 'hooks/committedClub/useCommittedClubTrackingSummary';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { useNavigate } from 'react-router-dom';
import { TrackingRow } from 'types';
import { DEFAULT_PROGRAM_NAME } from 'utils/checkInProgram';
import { parseDateTimeToDate } from 'utils/date-functions';
import notificationHandler from 'utils/notifications';

import AthleteNameCell from './components/cells/AthleteNameCell';
import ClubStatusBadgeCell from './components/cells/ClubStatusBadgeCell';
import ProgressBarCell from './components/cells/ProgressBarCell';
import StreakTooltipCell from './components/cells/StreakTooltipCell';
import ClubStatusFilter, { ALL_STATUSES } from './components/ClubStatusFilter';
import SummaryStats from './components/SummaryStats';
import TrackingHeader from './components/TrackingHeader';
import SettingsDrawer from '../Settings/components/SettingsDrawer';

const MONTH_PARAM_FORMAT = 'YYYY-MM';

const packageStatusToBadge = (packageStatus: TrackingRow['packageStatus']): 'ACTIVE' | 'HOLD' =>
  packageStatus === 'on_hold' ? 'HOLD' : 'ACTIVE';

const CommittedClubTracking = (): JSX.Element => {
  const navigate = useNavigate();
  const { hasPerm, session } = useAuth();
  const { errorNotification } = notificationHandler();
  const {
    trackCheckInProgramTrackingViewed,
    trackCheckInProgramTrackingFilterApplied,
    trackCheckInProgramTrackingCsvDownloaded,
  } = useStaffTracking();

  const today = useMemo(() => new Date(), []);
  const [selectedMonth, setSelectedMonth] = useState<Date>(today);
  const [programStatus, setProgramStatus] = useState<string>(ALL_STATUSES);
  const [settingsOpened, setSettingsOpened] = useState(false);

  const month = dayjs(selectedMonth).format(MONTH_PARAM_FORMAT);
  const isCurrentMonth = month === dayjs(today).format(MONTH_PARAM_FORMAT);
  const statusParam = programStatus === ALL_STATUSES ? undefined : programStatus;

  const programQuery = useCommittedClubProgram();
  const program = programQuery.data?.data.results?.[0];

  const tracking = useCommittedClubTracking({ month, programStatus: statusParam });
  const summary = useCommittedClubTrackingSummary({ month });
  const csvExport = useCommittedClubTrackingCsv();

  const rows = useMemo(
    () => tracking.data?.pages.flatMap(page => page.data.results ?? []) ?? [],
    [tracking.data],
  );

  const filteredCount = tracking.data?.pages[0]?.data.count;

  const earliestMonth = useMemo(() => {
    if (!program) return undefined;
    const created = parseDateTimeToDate(program.createdAt, session?.gym?.timezone);
    return new Date(created.getFullYear(), created.getMonth(), 1);
  }, [program, session?.gym?.timezone]);

  useEffect(() => {
    trackCheckInProgramTrackingViewed();
  }, [trackCheckInProgramTrackingViewed]);

  // No program yet: settings is the only meaningful destination.
  useEffect(() => {
    if (programQuery.isSuccess && !program) navigate('/check-in-program/settings');
  }, [programQuery.isSuccess, program, navigate]);

  const handleMonthChange = (value: Date | null): void => {
    if (!value) return;
    setSelectedMonth(value);
    // Spec 2.B / US-6: a past month defaults to completers so an owner can publish
    // a winners list without filtering by hand. Overridable.
    const isPast = dayjs(value).format(MONTH_PARAM_FORMAT) !== dayjs(today).format(MONTH_PARAM_FORMAT);
    setProgramStatus(isPast ? 'completed' : ALL_STATUSES);
    trackCheckInProgramTrackingFilterApplied('month', dayjs(value).format(MONTH_PARAM_FORMAT));
  };

  const handleStatusChange = (value: string): void => {
    setProgramStatus(value);
    trackCheckInProgramTrackingFilterApplied('program_status', value);
  };

  const handleCsvDownload = (): void => {
    csvExport.mutate(
      { month, programStatus: statusParam },
      {
        onSuccess: () => trackCheckInProgramTrackingCsvDownloaded(month),
        onError: error => {
          const message =
            error.response?.data?.message ||
            'An error occurred while generating the export. Please try again.';
          errorNotification('Export failed', message);
        },
      },
    );
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Athlete',
        accessor: 'name',
        Cell: ({ row }: { row: { original: TrackingRow } }) => (
          <AthleteNameCell name={row.original.name} isTrial={row.original.isTrial} />
        ),
      },
      {
        Header: 'Package Status',
        accessor: 'packageStatus',
        Cell: ({ row }: { row: { original: TrackingRow } }) => (
          <PackageStatusBadge status={packageStatusToBadge(row.original.packageStatus)} fullWidth={false} />
        ),
      },
      {
        Header: 'Progress',
        accessor: 'totalCheckins',
        Cell: ({ row }: { row: { original: TrackingRow } }) => (
          <ProgressBarCell totalCheckins={row.original.totalCheckins} required={row.original.required} />
        ),
      },
      {
        Header: 'Club Status',
        accessor: 'programStatus',
        Cell: ({ row }: { row: { original: TrackingRow } }) => (
          <ClubStatusBadgeCell programStatus={row.original.programStatus} />
        ),
      },
      {
        Header: 'Streak',
        accessor: 'streakLength',
        Cell: ({ row }: { row: { original: TrackingRow } }) => (
          <StreakTooltipCell
            streakLength={row.original.streakLength}
            streakStartMonth={row.original.streakStartMonth}
            month={month}
          />
        ),
      },
    ],
    [month],
  );

  if (programQuery.isLoading) return <LoadingOverlay visible />;

  const hasNoAthletes = tracking.isSuccess && rows.length === 0 && programStatus === ALL_STATUSES;

  return (
    <>
      <PageHeader>
        <TrackingHeader
          programName={program?.name ?? DEFAULT_PROGRAM_NAME}
          required={program?.currentThreshold ?? 0}
          onSettingsClick={() => setSettingsOpened(true)}
          canConfigure={hasPerm('CheckInProgramManage')}
        />
      </PageHeader>

      <Stack spacing='md' p='md' sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
        <Group position='apart' align='flex-end' noWrap>
          <Group spacing='md' align='flex-end' noWrap>
            <MonthPickerInput
              label='Month'
              value={selectedMonth}
              onChange={handleMonthChange}
              minDate={earliestMonth}
              maxDate={today}
              w={180}
            />
            <ClubStatusFilter value={programStatus} onChange={handleStatusChange} />
          </Group>
          <Group spacing='md' align='center' noWrap>
            <Button
              variant='default'
              leftIcon={<IconDownload size={16} />}
              onClick={handleCsvDownload}
              disabled={rows.length === 0}
              loading={csvExport.isLoading}
            >
              Export CSV
            </Button>
            <SummaryStats
              completed={summary.data?.data.completed}
              almostDone={summary.data?.data.almostDone}
              total={filteredCount}
              isLoading={summary.isLoading || tracking.isLoading}
            />
          </Group>
        </Group>

        {hasNoAthletes ? (
          <Center mih={240}>
            <Card withBorder radius='md' padding='xl' maw={640}>
              <Stack align='center' spacing='xs'>
                <Text weight={600}>Check-ins will appear here soon</Text>
                <Text color='dimmed' align='center'>
                  Data will start appearing here as athletes begin attending classes.
                </Text>
              </Stack>
            </Card>
          </Center>
        ) : (
          <Table
            fillHeight
            tableData={rows}
            columns={columns}
            isSuccess={tracking.isSuccess}
            isLoading={tracking.isLoading}
            isError={tracking.isError}
            hasNextPage={!!tracking.hasNextPage}
            fetchNextPage={tracking.fetchNextPage}
            isFetchingNextPage={tracking.isFetchingNextPage}
            EmptyState={
              <Text color='dimmed'>
                {isCurrentMonth
                  ? 'No athletes match this filter yet.'
                  : 'No athletes match this filter for the selected month.'}
              </Text>
            }
          />
        )}
      </Stack>

      <SettingsDrawer
        opened={settingsOpened}
        closeHandler={() => setSettingsOpened(false)}
        program={program}
        permission={hasPerm('CheckInProgramManage')}
      />
    </>
  );
};

export default CommittedClubTracking;
